import * as React from "react"
import { cn } from "../../lib/utils"
import { Button } from "../../components/ui/button"
import {
    LayoutDashboard,
    Settings,
    Users,
    PanelLeft,
    PanelRight,
    LucideIcon,
    User,
    UserCog,
    UserX,
    CreditCard,
    ArrowRightLeft,
    Banknote,
    Headset,
    Ticket,
    MessageSquare,
    MessageCircle,
    BookOpen,
    Smile,
    Shield,
    Lock,
    Activity,
    Monitor,
    BarChart,
    PieChart,
    FileDown,
    FileText,
    Menu,
    Cog,
    Eye,
    Folder,
    Heart,
    Handshake,
    X,
    Smartphone,
    ShieldAlert,
    Wallet,
    DollarSign,
    ClipboardList,
    ScrollText,
    Clock,
    TrendingUp,
} from "lucide-react"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../../components/ui/tooltip"
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../../components/ui/accordion"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAuth } from "../../context/AuthContext"
import { FULL_ADMIN_ROLES, STAFF_ROLES } from "../../components/ProtectedRoute"

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> { }

interface SidebarItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    icon: LucideIcon
    label: string
    isSidebarOpen: boolean
    isActive?: boolean
    expanded?: boolean
    href?: string
}

const SidebarItem = ({ icon: Icon, label, isSidebarOpen, isActive, className, href, ...props }: SidebarItemProps) => {
    const buttonContent = (
        <>
            <Icon className={cn("h-4 w-4", isSidebarOpen ? "mr-2" : "")} />
            {isSidebarOpen && <span>{label}</span>}
            {!isSidebarOpen && <span className="sr-only">{label}</span>}
        </>
    )

    const buttonClasses = cn(
        "w-full justify-start hover:bg-slate-100 hover:text-slate-900",
        !isSidebarOpen && "justify-center px-2",
        isActive && "bg-slate-100 text-slate-900",
        isSidebarOpen ? "pl-4" : "",
        className
    )

    if (href) {
        return (
            <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        className={buttonClasses}
                        asChild
                        {...props}
                    >
                        <Link to={href}>
                            {buttonContent}
                        </Link>
                    </Button>
                </TooltipTrigger>
                {!isSidebarOpen && (
                    <TooltipContent side="right" className="flex items-center gap-4">
                        {label}
                    </TooltipContent>
                )}
            </Tooltip>
        )
    }

    return (
        <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
                <Button
                    variant="ghost"
                    className={buttonClasses}
                    {...props}
                >
                    {buttonContent}
                </Button>
            </TooltipTrigger>
            {!isSidebarOpen && (
                <TooltipContent side="right" className="flex items-center gap-4">
                    {label}
                </TooltipContent>
            )}
        </Tooltip>
    )
}

export function Sidebar({ className }: SidebarProps) {
    const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
    const [isMobileOpen, setIsMobileOpen] = React.useState(false);
    const [openAccordion, setOpenAccordion] = React.useState<string>("");
    const { t } = useTranslation();
    const { user } = useAuth();

    const isAdmin = user && FULL_ADMIN_ROLES.includes(user.role);
    const isStaff = user && STAFF_ROLES.includes(user.role);
    const hasAdminAccess = isAdmin || isStaff;

    const handleAccordionTriggerClick = () => {
        if (!isSidebarOpen) {
            setIsSidebarOpen(true);
        }
    };

    // Close mobile sidebar when clicking a link
    const handleMobileLinkClick = () => {
        setIsMobileOpen(false);
    };

    return (
        <TooltipProvider>
            {/* Mobile Menu Button - Only visible on mobile */}
            <button
                onClick={() => setIsMobileOpen(true)}
                className="md:hidden fixed top-4 left-4 z-40 p-2 bg-white rounded-md shadow-md border"
                aria-label={t('common:aria.openMenu', 'Ouvri meni')}
            >
                <Menu className="h-5 w-5" />
            </button>

            {/* Mobile Backdrop */}
            {isMobileOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black/50 z-40"
                    onClick={() => setIsMobileOpen(false)}
                />
            )}

            {/* Sidebar Container */}
            <div className={cn(
                "bg-white border-r text-slate-900 h-screen transition-all duration-300 flex flex-col",
                // Mobile: fixed overlay, hidden by default
                // Desktop: sticky to stay in place while content scrolls
                "fixed md:sticky md:top-0 z-50 md:z-auto",
                "md:translate-x-0",
                isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
                // Width
                isSidebarOpen ? "w-64" : "w-20",
                className
            )}>
                <div className={cn("h-16 flex items-center border-b shrink-0", isSidebarOpen ? "px-4 md:px-6" : "px-0 justify-center")}>
                    {/* Mobile close button */}
                    <button
                        onClick={() => setIsMobileOpen(false)}
                        className="md:hidden p-1 mr-2 hover:bg-slate-100 rounded"
                        aria-label={t('common:aria.closeMenu', 'Fèmen meni')}
                    >
                        <X className="h-5 w-5" />
                    </button>

                    {isSidebarOpen && (
                        <>
                            <img src="/logo.webp" alt="" className="w-8 h-8" />
                            <h2 className="text-lg font-semibold tracking-tight ml-2 text-primary-500 mr-auto">
                                Bousanm
                            </h2>
                        </>
                    )}
                    {/* Desktop toggle button - hidden on mobile */}
                    <Button variant="ghost" size="icon" onClick={() => setIsSidebarOpen(!isSidebarOpen)} className={cn("hidden md:flex", !isSidebarOpen && "w-10 h-10")}>
                        {isSidebarOpen ? <PanelLeft className="h-5 w-5" /> : <PanelRight className="h-5 w-5" />}
                    </Button>
                </div>

                <div className="flex-1 overflow-y-auto py-4">
                    {hasAdminAccess && (
                        <div className="px-3 py-2">
                            {isSidebarOpen && (
                                <h3 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {t('dashboard:sidebar.sections.general')}
                                </h3>
                            )}
                            <div className="space-y-1">
                                <SidebarItem icon={LayoutDashboard} label={t('dashboard:sidebar.items.dashboard')} href="/dashboard" isSidebarOpen={isSidebarOpen} />
                            </div>
                        </div>
                    )}

                    {hasAdminAccess && (
                        <div className="px-3 py-2">
                            {isSidebarOpen && (
                                <h3 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {t('dashboard:sidebar.sections.comptes')}
                                </h3>
                            )}
                            <div className="space-y-1">
                                <Accordion type="single" collapsible className="w-full" value={openAccordion} onValueChange={setOpenAccordion}>
                                    <AccordionItem value="comptes" className="border-b-0">
                                        <AccordionTrigger
                                            className={cn(
                                                "py-2 hover:bg-slate-100 hover:no-underline rounded-md px-4 text-sm font-medium",
                                                !isSidebarOpen && "justify-center px-2 [&>svg]:hidden"
                                            )}
                                            onClick={handleAccordionTriggerClick}
                                        >
                                            <div className="flex items-center">
                                                <Users className={cn("h-4 w-4", isSidebarOpen ? "mr-2" : "")} />
                                                {isSidebarOpen && <span>{t('dashboard:sidebar.items.gestionComptes')}</span>}
                                            </div>
                                            {/* Standard chevron is inside Trigger, we hide it via CSS/prop if collapsed if needed, but flex-1 logic keeps it away */}
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-0 pl-10">
                                            <div className="space-y-1 mt-1">
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/clients">
                                                        <User className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.clients')}
                                                    </Link>
                                                </Button>
                                                {isAdmin && (
                                                    <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                        <Link to="/agents">
                                                            <UserCog className="mr-2 h-4 w-4" />
                                                            {t('dashboard:sidebar.items.agents')}
                                                        </Link>
                                                    </Button>
                                                )}
                                                {isAdmin && (
                                                    <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                        <Link to="/agent-monitor">
                                                            <TrendingUp className="mr-2 h-4 w-4" />
                                                            Monitor Ajan
                                                        </Link>
                                                    </Button>
                                                )}
                                                {isAdmin && (
                                                    <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                        <Link to="/unvalidated">
                                                            <UserX className="mr-2 h-4 w-4" />
                                                            {t('dashboard:sidebar.items.unvalidated')}
                                                        </Link>
                                                    </Button>
                                                )}
                                                {isAdmin && (
                                                    <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                        <Link to="/cards">
                                                            <CreditCard className="mr-2 h-4 w-4" />
                                                            {t('dashboard:sidebar.items.cards')}
                                                        </Link>
                                                    </Button>
                                                )}
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        </div>
                    )}

                    {isAdmin && (
                        <div className="px-3 py-2">
                            {isSidebarOpen && (
                                <h3 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Kat VISA × HyperPay
                                </h3>
                            )}
                            <div className="space-y-1">
                                <Accordion type="single" collapsible className="w-full" value={openAccordion} onValueChange={setOpenAccordion}>
                                    <AccordionItem value="visa-card" className="border-b-0">
                                        <AccordionTrigger
                                            className={cn(
                                                "py-2 hover:bg-slate-100 hover:no-underline rounded-md px-4 text-sm font-medium",
                                                !isSidebarOpen && "justify-center px-2 [&>svg]:hidden"
                                            )}
                                            onClick={handleAccordionTriggerClick}
                                        >
                                            <div className="flex items-center">
                                                <Wallet className={cn("h-4 w-4", isSidebarOpen ? "mr-2" : "")} />
                                                {isSidebarOpen && <span>Jere Kat VISA</span>}
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-0 pl-10">
                                            <div className="space-y-1 mt-1">
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/visa/dashboard">
                                                        <LayoutDashboard className="mr-2 h-4 w-4" />
                                                        Dashboard
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/visa/applications">
                                                        <ClipboardList className="mr-2 h-4 w-4" />
                                                        Aplikasyon
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/visa/cards">
                                                        <CreditCard className="mr-2 h-4 w-4" />
                                                        Kat Aktif
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/visa/recharges">
                                                        <DollarSign className="mr-2 h-4 w-4" />
                                                        Rechaj
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/visa/transactions">
                                                        <ArrowRightLeft className="mr-2 h-4 w-4" />
                                                        Tranzaksyon
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/visa/logs">
                                                        <ScrollText className="mr-2 h-4 w-4" />
                                                        Audit Logs
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/visa/config">
                                                        <Settings className="mr-2 h-4 w-4" />
                                                        Paramèt
                                                    </Link>
                                                </Button>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        </div>
                    )}

                    {hasAdminAccess && (
                        <div className="px-3 py-2">
                            {isSidebarOpen && (
                                <h3 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {t('dashboard:sidebar.sections.solide')}
                                </h3>
                            )}
                            <div className="space-y-1">
                                <Accordion type="single" collapsible className="w-full" value={openAccordion} onValueChange={setOpenAccordion}>
                                    <AccordionItem value="bousanm-solide" className="border-b-0">
                                        <AccordionTrigger
                                            className={cn(
                                                "py-2 hover:bg-slate-100 hover:no-underline rounded-md px-4 text-sm font-medium",
                                                !isSidebarOpen && "justify-center px-2 [&>svg]:hidden"
                                            )}
                                            onClick={handleAccordionTriggerClick}
                                        >
                                            <div className="flex items-center">
                                                <Handshake className={cn("h-4 w-4", isSidebarOpen ? "mr-2" : "")} />
                                                {isSidebarOpen && <span>{t('dashboard:sidebar.items.gestionSolide')}</span>}
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-0 pl-10">
                                            <div className="space-y-1 mt-1">
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/bousanm-solide/dashboard">
                                                        <LayoutDashboard className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.dashboard')}
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/bousanm-solide/clients">
                                                        <Users className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.clients')}
                                                    </Link>
                                                </Button>
                                                {isAdmin && (
                                                    <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                        <Link to="/bousanm-solide/agents">
                                                            <UserCog className="mr-2 h-4 w-4" />
                                                            {t('dashboard:sidebar.items.agents')}
                                                        </Link>
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/bousanm-solide/transactions">
                                                        <ArrowRightLeft className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.transactions')}
                                                    </Link>
                                                </Button>
                                                {isAdmin && (
                                                    <>
                                                        <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                            <Link to="/bousanm-solide/plans">
                                                                <Folder className="mr-2 h-4 w-4" />
                                                                {t('dashboard:planSolide.plan')}
                                                            </Link>
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                            <Link to="/bousanm-solide/rapports">
                                                                <BarChart className="mr-2 h-4 w-4" />
                                                                {t('dashboard:sidebar.sections.rapports')}
                                                            </Link>
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        </div>
                    )}

                    {hasAdminAccess && (
                        <div className="px-3 py-2">
                            {isSidebarOpen && (
                                <h3 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {t('dashboard:sidebar.sections.transactions')}
                                </h3>
                            )}
                            <div className="space-y-1">
                                <Accordion type="single" collapsible className="w-full" value={openAccordion} onValueChange={setOpenAccordion}>
                                    <AccordionItem value="transactions" className="border-b-0">
                                        <AccordionTrigger
                                            className={cn(
                                                "py-2 hover:bg-slate-100 hover:no-underline rounded-md px-4 text-sm font-medium",
                                                !isSidebarOpen && "justify-center px-2 [&>svg]:hidden"
                                            )}
                                            onClick={handleAccordionTriggerClick}
                                        >
                                            <div className="flex items-center">
                                                <ArrowRightLeft className={cn("h-4 w-4", isSidebarOpen ? "mr-2" : "")} />
                                                {isSidebarOpen && <span>{t('dashboard:sidebar.items.gestionTransactions')}</span>}
                                            </div>
                                            {/* Standard chevron is inside Trigger, we hide it via CSS/prop if collapsed if needed, but flex-1 logic keeps it away */}
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-0 pl-10">
                                            <div className="space-y-1 mt-1">
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/transactions">
                                                        <ArrowRightLeft className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.transactions')}
                                                    </Link>
                                                </Button>
                                                {isAdmin && (
                                                    <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                        <Link to="/payments">
                                                            <Banknote className="mr-2 h-4 w-4" />
                                                            {t('dashboard:sidebar.items.payments')}
                                                        </Link>
                                                    </Button>
                                                )}
                                                {isAdmin && (
                                                    <>
                                                        <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                            <Link to="/administration/reviews">
                                                                <ShieldAlert className="mr-2 h-4 w-4" />
                                                                {t('dashboard:sidebar.items.validation')}
                                                            </Link>
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                            <Link to="/administration/rapports">
                                                                <FileText className="mr-2 h-4 w-4" />
                                                                {t('dashboard:sidebar.items.validationReports')}
                                                            </Link>
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                            <Link to="/transactions/retrait-requests">
                                                                <Clock className="mr-2 h-4 w-4" />
                                                                Demann Retrè
                                                            </Link>
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        </div>
                    )}

                    {isAdmin && (
                        <div className="px-3 py-2">
                            {isSidebarOpen && (
                                <h3 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {t('dashboard:sidebar.sections.rapports')}
                                </h3>
                            )}
                            <div className="space-y-1">
                                <Accordion type="single" collapsible className="w-full" value={openAccordion} onValueChange={setOpenAccordion}>
                                    <AccordionItem value="analytics" className="border-b-0">
                                        <AccordionTrigger
                                            className={cn(
                                                "py-2 hover:bg-slate-100 hover:no-underline rounded-md px-4 text-sm font-medium",
                                                !isSidebarOpen && "justify-center px-2 [&>svg]:hidden"
                                            )}
                                            onClick={handleAccordionTriggerClick}
                                        >
                                            <div className="flex items-center">
                                                <BarChart className={cn("h-4 w-4", isSidebarOpen ? "mr-2" : "")} />
                                                {isSidebarOpen && <span>{t('dashboard:sidebar.items.analytics')}</span>}
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-0 pl-10">
                                            <div className="space-y-1 mt-1">
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/surveillance">
                                                        <Eye className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.surveillance')}
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <PieChart className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.overview')}
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <FileText className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.financialReports')}
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <FileDown className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.dataExport')}
                                                </Button>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        </div>
                    )}


                    {isAdmin && (
                        <div className="px-3 py-2">
                            {isSidebarOpen && (
                                <h3 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {t('dashboard:sidebar.sections.support')}
                                </h3>
                            )}
                            <div className="space-y-1">
                                <Accordion type="single" collapsible className="w-full" value={openAccordion} onValueChange={setOpenAccordion}>
                                    <AccordionItem value="support" className="border-b-0">
                                        <AccordionTrigger
                                            className={cn(
                                                "py-2 hover:bg-slate-100 hover:no-underline rounded-md px-4 text-sm font-medium",
                                                !isSidebarOpen && "justify-center px-2 [&>svg]:hidden"
                                            )}
                                            onClick={handleAccordionTriggerClick}
                                        >
                                            <div className="flex items-center">
                                                <Headset className={cn("h-4 w-4", isSidebarOpen ? "mr-2" : "")} />
                                                {isSidebarOpen && <span>{t('dashboard:sidebar.items.customerService')}</span>}
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-0 pl-10">
                                            <div className="space-y-1 mt-1">
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <Ticket className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.tickets')}
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <MessageSquare className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.emailMessages')}
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <MessageCircle className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.liveChat')}
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <BookOpen className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.faqHelp')}
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <Smile className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.satisfaction')}
                                                </Button>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        </div>
                    )}


                    {isAdmin && (
                        <div className="px-3 py-2">
                            {isSidebarOpen && (
                                <h3 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {t('dashboard:sidebar.sections.integrations')}
                                </h3>
                            )}
                            <div className="space-y-1">
                                <SidebarItem icon={LayoutDashboard} label="Slack" isSidebarOpen={isSidebarOpen} />
                                <SidebarItem icon={Settings} label="GitHub" isSidebarOpen={isSidebarOpen} />
                            </div>
                        </div>
                    )}

                    {isAdmin && (
                        <div className="px-3 py-2">
                            {isSidebarOpen && (
                                <h3 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {t('dashboard:sidebar.sections.system')}
                                </h3>
                            )}
                            <div className="space-y-1">
                                <Accordion type="single" collapsible className="w-full" value={openAccordion} onValueChange={setOpenAccordion}>
                                    <AccordionItem value="admin" className="border-b-0">
                                        <AccordionTrigger
                                            className={cn(
                                                "py-2 hover:bg-slate-100 hover:no-underline rounded-md px-4 text-sm font-medium",
                                                !isSidebarOpen && "justify-center px-2 [&>svg]:hidden"
                                            )}
                                            onClick={handleAccordionTriggerClick}
                                        >
                                            <div className="flex items-center">
                                                <Shield className={cn("h-4 w-4", isSidebarOpen ? "mr-2" : "")} />
                                                {isSidebarOpen && <span>{t('dashboard:sidebar.items.admin')}</span>}
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-0 pl-10">
                                            <div className="space-y-1 mt-1">
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <Lock className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.roles')}
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <Activity className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.audit')}
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/administration/fees">
                                                        <Banknote className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.feesManagement')}
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/administration/users">
                                                        <Users className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.users')}
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8">
                                                    <Monitor className="mr-2 h-4 w-4" />
                                                    {t('dashboard:sidebar.items.monitoring')}
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/configuration">
                                                        <Cog className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.config')}
                                                    </Link>
                                                </Button>
                                                <Button variant="ghost" size="sm" className="w-full justify-start h-8" asChild>
                                                    <Link to="/configuration/mobile">
                                                        <Smartphone className="mr-2 h-4 w-4" />
                                                        {t('dashboard:sidebar.items.mobileApp')}
                                                    </Link>
                                                </Button>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        </div>
                    )}
                </div>

                {/* <div className="px-3 py-4 mt-auto">
                    <SidebarItem icon={Settings} label="Paramètres" isSidebarOpen={isSidebarOpen} />
                </div> */}
            </div>
        </TooltipProvider>
    )
}
